import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ButtonControl, Loading } from "spark-core-dx/components";
import { useResourceDomainContext } from "../../../ResourceDomainProvider";
import CardGridControl from "../../CardGridControl";
import {
  buildWizardValidationError,
  getFirst,
  getId,
  getProductResourceDefaults,
  getOperationItem,
  getOperationItems,
  getSetupFormFieldStateOverrides,
  getValue,
  parseSetupFieldStateList,
  readValue,
  requireSuccessfulServiceResponse,
  resolveProductResourceEdiTransactionSets,
  validateRequiredCardGridRows
} from "../resourceConfigWizardUtils";
import {
  getEditableDraftSchemaVersionId,
  getEntityObjectDisplayName,
  getEntitySchemaMode,
  getEntitySchemaModeLabel,
  getSchemaIdFromValues
} from "./entitySchemaStepUtils";
import { LinkedStepHeader } from "./WizardStepCommon";

const MODIFY_ENTITY_OPERATION = "ModifyEntity";
const ENTITY_OBJECT_FIELD_MAPPING_OPERATION = "EntityObjectFieldMapping";
const ENTITY_OBJECT_ITEM_NAME = "EntityObject";
const ENTITY_OBJECT_DRAFT_PREFIX = "entity-object-draft";
const ENTITY_OBJECT_DEFAULT_TYPE = 2;
const ENTITY_OBJECT_DEFAULT_GRID_TYPE = 1;
const ENTITY_OBJECT_DEFAULT_FILTER_TYPE = 1;
const ENTITY_OBJECT_CARD_FIELD_NAMES = ["DisplayName", "ObjectName", "Fields", "IsActive"];
const OUTBOUND_EDI_ENTITY_OBJECT_PATTERN = "outboundedientityobjectconfigurator";
const OUTBOUND_EDI_FILTER_MODE = "outboundedi";
const EDI_DOMAIN_FIELD = "EdiDomain";
const EDI_DIRECTION_FIELD = "TypicalDirection";
const EDI_TRANSACTION_SET_FIELD = "EDITransactionSet";
const EDI_OBJECT_TYPE_FIELD = "EDIObjectType";
const EDI_WRITE_MODE_FIELD = "EDIWriteMode";

const toSafeName = (value) => {
  const normalized = String(value || "").trim().replace(/[^A-Za-z0-9_-]+/g, "_").replace(/^[_-]+|[_-]+$/g, "");
  if (!normalized) return "EntityObject";
  return /^[A-Za-z]/.test(normalized) ? normalized : `Entity_${normalized}`;
};

const normalizeText = (value) => String(value ?? "").trim().toLowerCase();
const getMetadataFields = (metadata) => getValue(metadata, "Fields", "fields") || [];
const getFieldName = (field) => readValue(field, "FieldName", "fieldName") || "";
const getFieldType = (field) => normalizeText(readValue(field, "FieldType", "fieldType"));
const getFieldTab = (field) => normalizeText(readValue(field, "TabId", "tabId")).split("|")[0] || "";
const getFieldDefault = (field, fallback) => {
  const configuredDefault = readValue(field, "DefaultSelection", "defaultSelection", "DefaultValue", "defaultValue");
  return configuredDefault === undefined || configuredDefault === null || configuredDefault === "" ? fallback : configuredDefault;
};
const normalizeLookupText = (value) => String(value ?? "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "");
const getCustomFields = (item) => readValue(item, "CustomFields", "customFields") || {};
const getCustomFieldValue = (item, fieldName) => readValue(getCustomFields(item), fieldName, fieldName.charAt(0).toLowerCase() + fieldName.slice(1));
const normalizeTransactionSet = (value) => {
  const text = String(value ?? "").trim();
  if (!text) return "";
  const match = text.match(/(^|\D)(\d{3})(\D|$)/);
  return match ? match[2] : text.toUpperCase();
};
const isOutboundEdiStep = (context, defaults = {}) => (
  normalizeLookupText(readValue(context?.sectionStep, "UiPatternKey", "uiPatternKey")) === OUTBOUND_EDI_ENTITY_OBJECT_PATTERN
  || normalizeLookupText(readValue(defaults, "entityObjectFilterMode", "EntityObjectFilterMode", "entityObjectMode", "EntityObjectMode")) === OUTBOUND_EDI_FILTER_MODE
);
const filterHiddenFields = (fields = [], hiddenFieldNames = []) => {
  const hidden = new Set(hiddenFieldNames.map(normalizeLookupText).filter(Boolean));
  if (!hidden.size) return fields;
  return fields.filter((field) => !hidden.has(normalizeLookupText(getFieldName(field))));
};
const getDefaultCustomFields = (defaults = {}) => {
  const customFields = readValue(defaults, "entityObjectDefaultCustomFields", "EntityObjectDefaultCustomFields");
  return customFields && typeof customFields === "object" && !Array.isArray(customFields) ? customFields : {};
};
const getDefaultEntityObjectType = (defaults = {}, fallback) => {
  const configured = readValue(defaults, "entityObjectDefaultEntityObjectType", "EntityObjectDefaultEntityObjectType", "entityObjectDefaultObjectType", "EntityObjectDefaultObjectType");
  if (configured === null || String(configured ?? "").trim().toLowerCase() === "null" || String(configured ?? "").trim() === "") {
    return isOutboundEdiStep(null, defaults) ? null : fallback;
  }
  return configured === undefined ? fallback : configured;
};
const getDefaultBoolean = (defaults = {}, fieldName, fallback) => {
  const value = readValue(defaults, fieldName, fieldName.charAt(0).toUpperCase() + fieldName.slice(1));
  if (value === undefined || value === null || value === "") return fallback;
  if (typeof value === "boolean") return value;
  return String(value).trim().toLowerCase() === "true";
};
const getEdiEntityLabel = (defaults = {}) =>
  readValue(defaults, "entityObjectLabel", "EntityObjectLabel") || "EDI Entity Object";
const matchesOutboundEdiEntityObject = (entityObject, transactionSets = []) => {
  const isEdi = normalizeLookupText(getCustomFieldValue(entityObject, EDI_DOMAIN_FIELD)) === "edi"
    || getCustomFieldValue(entityObject, EDI_OBJECT_TYPE_FIELD)
    || getCustomFieldValue(entityObject, EDI_WRITE_MODE_FIELD)
    || getCustomFieldValue(entityObject, EDI_TRANSACTION_SET_FIELD)
    || String(readValue(entityObject, "ObjectName", "objectName", "SystemName", "systemName") || "").toLowerCase().startsWith("x12_");
  if (!isEdi) return false;
  const direction = normalizeLookupText(getCustomFieldValue(entityObject, EDI_DIRECTION_FIELD));
  if (direction && direction !== "outbound" && direction !== "bidirectional") return false;
  if (!transactionSets.length) return true;
  const entityTransactionSet = normalizeTransactionSet(getCustomFieldValue(entityObject, EDI_TRANSACTION_SET_FIELD));
  if (entityTransactionSet && transactionSets.includes(entityTransactionSet)) return true;
  const objectText = `${readValue(entityObject, "ObjectName", "objectName", "SystemName", "systemName", "DisplayName", "displayName") || ""}`;
  return transactionSets.some((transactionSet) => objectText.includes(transactionSet));
};
const isEmptyOrNone = (value) =>
  value === undefined ||
  value === null ||
  value === "" ||
  String(value).trim() === "0";
const getDefaultedValue = (source, field, fallback, ...keys) => {
  const value = readValue(source, ...keys);
  return isEmptyOrNone(value) ? getFieldDefault(field, fallback) : value;
};
const findMetadataField = (metadata, fieldName) => {
  const normalizedFieldName = normalizeText(fieldName);
  return getMetadataFields(metadata).find((field) => normalizeText(getFieldName(field)) === normalizedFieldName) || null;
};
const resolveEntityObjectMetadata = (metadataSource) =>
  getOperationItems(metadataSource).find((item) =>
    normalizeText(readValue(item, "Name", "name", "ItemName", "itemName")) === normalizeText(ENTITY_OBJECT_ITEM_NAME)
  ) || null;
const buildEntityObjectCardFields = (metadata) =>
  ENTITY_OBJECT_CARD_FIELD_NAMES
    .map((fieldName) => findMetadataField(metadata, fieldName))
    .filter(Boolean);
const buildEntityObjectValidationFields = (metadata) =>
  getMetadataFields(metadata).filter((field) => {
    if (!readValue(field, "IsRequired", "isRequired")) return false;
    if (getFieldTab(field) === "metadata") return false;
    const fieldType = getFieldType(field);
    return fieldType !== "label" && fieldType !== "subgrid" && fieldType !== "subitem";
  });

const getCurrentBackUrlQueryParams = () => {
  if (typeof window === "undefined" || !window.location) return undefined;
  const backUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  return backUrl ? { backUrl } : undefined;
};
const getResourceModeHardRedirectQueryParams = () => {
  if (typeof window === "undefined" || !window.location) return undefined;
  const redirectBaseUrl = window.location.origin || "";
  if (!redirectBaseUrl) return getCurrentBackUrlQueryParams();
  return {
    ...(getCurrentBackUrlQueryParams() || {}),
    uiRedirectMode: "hard",
    redirectBaseUrl
  };
};

const loadSchemaById = async (schemaMgr, schemaId, coreState) => {
  if (!schemaId) return null;
  if (schemaMgr?.getItem) {
    const result = await schemaMgr.getItem(schemaId);
    requireSuccessfulServiceResponse(result, "Entity Schema could not be loaded.", coreState);
    return getOperationItem(result) || null;
  }
  if (schemaMgr?.Query) {
    const result = await schemaMgr.Query({
      model: "x => x.IsDeleted == false && (x._id == schemaId || x.SchemaId == schemaId || x.UniqueId == schemaId)",
      params: { schemaId },
      id: schemaId
    });
    requireSuccessfulServiceResponse(result, "Entity Schema could not be loaded.", coreState);
    return getOperationItems(result).find((item) =>
      getId(item) === schemaId
      || readValue(item, "SchemaId", "schemaId", "UniqueId", "uniqueId") === schemaId
    ) || null;
  }
  throw new Error("Schema service is not available.");
};

const getSchemaRecordId = (schema) => readValue(schema, "SchemaId", "schemaId", "UniqueId", "uniqueId") || getId(schema);

const getEntityObjectKey = (entityObject, index) =>
  readValue(entityObject, "_tempKey", "EntityObjectDraftKey", "entityObjectDraftKey")
  || getId(entityObject)
  || readValue(entityObject, "ObjectName", "objectName")
  || `${ENTITY_OBJECT_DRAFT_PREFIX}-${index}`;

const getEntityObjectFieldCount = (entityObject) => (getValue(entityObject, "Fields", "fields") || []).length;

const withEntityObjectCardValues = (entityObject) => ({
  ...(entityObject || {}),
  GridType: isEmptyOrNone(readValue(entityObject, "GridType", "gridType"))
    ? ENTITY_OBJECT_DEFAULT_GRID_TYPE
    : readValue(entityObject, "GridType", "gridType"),
  FilterType: isEmptyOrNone(readValue(entityObject, "FilterType", "filterType"))
    ? ENTITY_OBJECT_DEFAULT_FILTER_TYPE
    : readValue(entityObject, "FilterType", "filterType")
});

const withoutEntityObjectCardValues = (entityObject) => {
  const {
    _tempKey,
    EntityObjectDraftKey,
    entityObjectDraftKey,
    ...rest
  } = entityObject || {};
  return rest;
};

const getUniqueEntityObjectName = (existingObjects, baseDisplayName = "New Entity Object") => {
  const existingObjectNames = new Set(existingObjects.map((item) => String(readValue(item, "ObjectName", "objectName") || "").toLowerCase()));
  const existingDisplayNames = new Set(existingObjects.map((item) => String(readValue(item, "DisplayName", "displayName") || "").toLowerCase()));
  let index = 1;
  while (index < 1000) {
    const displayName = index === 1 ? baseDisplayName : `${baseDisplayName} ${index}`;
    const objectName = index === 1 ? toSafeName(baseDisplayName) : `${toSafeName(baseDisplayName)}${index}`;
    if (!existingObjectNames.has(objectName.toLowerCase()) && !existingDisplayNames.has(displayName.toLowerCase())) {
      return { displayName, objectName };
    }
    index += 1;
  }
  return {
    displayName: `${baseDisplayName} ${Date.now()}`,
    objectName: `${toSafeName(baseDisplayName)}${Date.now()}`
  };
};

const createDefaultEntityObjectField = () => ({
  IsNew: true,
  IsActive: true,
  IsDeleted: false,
  ObjectFieldName: "Name",
  SourceFieldName: "",
  DisplayName: "Name",
  SystemName: "Name",
  DataTypeName: "String",
  Format: "STRING",
  FieldType: "TextBox",
  Sequence: 1
});

const ensureEntityObjectDefaults = (entityObject, schema, schemaVersionId, metadata, defaults = {}) => {
  const objectName = toSafeName(readValue(entityObject, "ObjectName", "objectName") || readValue(entityObject, "DisplayName", "displayName"));
  const displayName = readValue(entityObject, "DisplayName", "displayName") || objectName;
  const fields = getValue(entityObject, "Fields", "fields");
  const entityObjectTypeField = findMetadataField(metadata, "EntityObjectType");
  const deploySequenceField = findMetadataField(metadata, "DeploySequence");
  const gridTypeField = findMetadataField(metadata, "GridType");
  const filterTypeField = findMetadataField(metadata, "FilterType");
  const customFields = {
    ...getDefaultCustomFields(defaults),
    ...getCustomFields(entityObject)
  };
  const entityObjectTypeValue = readValue(entityObject, "EntityObjectType", "entityObjectType");
  return {
    ...withoutEntityObjectCardValues(entityObject),
    IsNew: !getId(entityObject),
    IsActive: readValue(entityObject, "IsActive", "isActive") !== false,
    IsDeleted: readValue(entityObject, "IsDeleted", "isDeleted") === true,
    SchemaId: readValue(entityObject, "SchemaId", "schemaId") || getSchemaRecordId(schema),
    SchemaVersionId: readValue(entityObject, "SchemaVersionId", "schemaVersionId") || schemaVersionId,
    EntityObjectType: entityObjectTypeValue === undefined || entityObjectTypeValue === null || entityObjectTypeValue === ""
      ? getDefaultEntityObjectType(defaults, getFieldDefault(entityObjectTypeField, ENTITY_OBJECT_DEFAULT_TYPE))
      : entityObjectTypeValue,
    ObjectName: objectName,
    DisplayName: displayName,
    SystemName: readValue(entityObject, "SystemName", "systemName") || objectName,
    Description: readValue(entityObject, "Description", "description") || "",
    SourceObjectOfRecordOnly: getDefaultBoolean(defaults, "entityObjectDefaultSourceObjectOfRecordOnly", readValue(entityObject, "SourceObjectOfRecordOnly", "sourceObjectOfRecordOnly") !== false),
    DisableMasterRecords: getDefaultBoolean(defaults, "entityObjectDefaultDisableMasterRecords", readValue(entityObject, "DisableMasterRecords", "disableMasterRecords") !== false),
    AutoFinalize: getDefaultBoolean(defaults, "entityObjectDefaultAutoFinalize", readValue(entityObject, "AutoFinalize", "autoFinalize") !== false),
    DeploySequence: readValue(entityObject, "DeploySequence", "deploySequence") || getFieldDefault(deploySequenceField, 999),
    GridType: getDefaultedValue(entityObject, gridTypeField, ENTITY_OBJECT_DEFAULT_GRID_TYPE, "GridType", "gridType"),
    FilterType: getDefaultedValue(entityObject, filterTypeField, ENTITY_OBJECT_DEFAULT_FILTER_TYPE, "FilterType", "filterType"),
    CustomFields: customFields,
    customFields,
    Fields: Array.isArray(fields) && fields.length ? fields : [createDefaultEntityObjectField()]
  };
};

const createEntityObjectDraft = (schema, schemaVersionId, existingObjects, sequence, defaults = {}) => {
  const { displayName, objectName } = getUniqueEntityObjectName(
    existingObjects,
    readValue(defaults, "entityObjectDefaultBaseDisplayName", "EntityObjectDefaultBaseDisplayName") || "New Entity Object"
  );
  const customFields = getDefaultCustomFields(defaults);
  return withEntityObjectCardValues({
    _tempKey: `${ENTITY_OBJECT_DRAFT_PREFIX}-${Date.now()}-${sequence}`,
    IsNew: true,
    IsActive: true,
    IsDeleted: false,
    SchemaId: getSchemaRecordId(schema),
    SchemaVersionId: schemaVersionId,
    EntityObjectType: getDefaultEntityObjectType(defaults, ENTITY_OBJECT_DEFAULT_TYPE),
    ObjectName: objectName,
    DisplayName: displayName,
    SystemName: objectName,
    Description: "",
    SourceObjectOfRecordOnly: getDefaultBoolean(defaults, "entityObjectDefaultSourceObjectOfRecordOnly", true),
    DisableMasterRecords: getDefaultBoolean(defaults, "entityObjectDefaultDisableMasterRecords", true),
    AutoFinalize: getDefaultBoolean(defaults, "entityObjectDefaultAutoFinalize", true),
    GridType: ENTITY_OBJECT_DEFAULT_GRID_TYPE,
    FilterType: ENTITY_OBJECT_DEFAULT_FILTER_TYPE,
    CustomFields: customFields,
    customFields,
    Fields: [createDefaultEntityObjectField()]
  });
};

const EntityObjectConfiguratorStep = ({ context, onSave, onContinue, registerStepController, onDirty }) => {
  const { wizard, values = {}, readOnly, saving } = context;
  const productResourceDefaults = context.productResourceDefaults || getProductResourceDefaults(wizard);
  const outboundEdiMode = isOutboundEdiStep(context, productResourceDefaults);
  const productEdiTransactionSets = useMemo(
    () => resolveProductResourceEdiTransactionSets(productResourceDefaults),
    [productResourceDefaults]
  );
  const resourceDomainContext = useResourceDomainContext();
  const coreState = resourceDomainContext?.coreState;
  const schemaMgr = resourceDomainContext?.managers?.schema;
  const entityObjectMgr = resourceDomainContext?.managers?.entityObject;
  const schemaId = getSchemaIdFromValues(wizard, values);
  const [schema, setSchema] = useState(null);
  const [entityObjects, setEntityObjects] = useState([]);
  const [entityObjectMetadata, setEntityObjectMetadata] = useState(null);
  const [loading, setLoading] = useState(Boolean(schemaId));
  const [entityObjectsLoadedKey, setEntityObjectsLoadedKey] = useState("");
  const [metadataLoading, setMetadataLoading] = useState(true);
  const [localError, setLocalError] = useState("");
  const [actionBusyKey, setActionBusyKey] = useState("");
  const [selectedEntityObjectKey, setSelectedEntityObjectKey] = useState("");
  const [cardEditorOpen, setCardEditorOpen] = useState(false);
  const schemaRef = useRef(schema);
  const entityObjectsRef = useRef(entityObjects);
  const entityObjectMetadataRef = useRef(entityObjectMetadata);
  const schemaMgrRef = useRef(schemaMgr);
  const entityObjectMgrRef = useRef(entityObjectMgr);
  const coreStateRef = useRef(coreState);
  const productResourceDefaultsRef = useRef(productResourceDefaults);
  const entityObjectsLoadKeyRef = useRef("");
  const pendingEntityObjectKeysRef = useRef(new Set());
  const dirtyEntityObjectKeysRef = useRef(new Set());
  const draftSequenceRef = useRef(0);
  schemaRef.current = schema;
  entityObjectsRef.current = entityObjects;
  entityObjectMetadataRef.current = entityObjectMetadata;
  schemaMgrRef.current = schemaMgr;
  entityObjectMgrRef.current = entityObjectMgr;
  coreStateRef.current = coreState;
  productResourceDefaultsRef.current = productResourceDefaults;

  const schemaVersionId = getEditableDraftSchemaVersionId(schema);
  const hiddenFieldNames = useMemo(() => parseSetupFieldStateList(
    getSetupFormFieldStateOverrides(productResourceDefaults, ENTITY_OBJECT_ITEM_NAME).IsHidden
  ), [productResourceDefaults]);
  const entityObjectFormFields = useMemo(
    () => filterHiddenFields(getMetadataFields(entityObjectMetadata), hiddenFieldNames),
    [entityObjectMetadata, hiddenFieldNames]
  );
  const entityObjectCardFields = useMemo(() => buildEntityObjectCardFields(entityObjectMetadata), [entityObjectMetadata]);
  const entityObjectValidationFields = useMemo(
    () => filterHiddenFields(buildEntityObjectValidationFields(entityObjectMetadata), hiddenFieldNames),
    [entityObjectMetadata, hiddenFieldNames]
  );
  const entityObjectMetadataServiceReady = Boolean(entityObjectMgr?.GetMetadata);
  const schemaServiceReady = Boolean(schemaMgr?.getItem || schemaMgr?.Query);
  const entityObjectQueryServiceReady = Boolean(entityObjectMgr?.Query);
  const isLoading = loading || metadataLoading || Boolean(schemaId && entityObjectsLoadedKey !== schemaId);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      const activeEntityObjectMgr = entityObjectMgrRef.current;
      if (!activeEntityObjectMgr?.GetMetadata) {
        if (mounted) setMetadataLoading(false);
        return;
      }

      setMetadataLoading(true);
      try {
        const result = await activeEntityObjectMgr.GetMetadata({});
        requireSuccessfulServiceResponse(result, "Entity Object metadata could not be loaded.", coreStateRef.current);
        const metadata = resolveEntityObjectMetadata(result) || resolveEntityObjectMetadata(activeEntityObjectMgr.uiMetadata);
        if (!metadata) throw new Error("Entity Object metadata could not be loaded.");
        if (mounted) setEntityObjectMetadata(metadata);
      } catch (ex) {
        if (mounted) {
          setEntityObjectMetadata(null);
          setLocalError(ex?.message || "Entity Object metadata could not be loaded.");
        }
      } finally {
        if (mounted) setMetadataLoading(false);
      }
    };
    void load();
    return () => {
      mounted = false;
    };
  }, [entityObjectMetadataServiceReady]);

  const loadEntityObjects = useCallback(async (loadedSchema = schemaRef.current) => {
    const activeEntityObjectMgr = entityObjectMgrRef.current;
    if (!loadedSchema || !activeEntityObjectMgr?.Query) return [];
    const activeVersionId = getEditableDraftSchemaVersionId(loadedSchema);
    if (!activeVersionId) return [];
    const result = await activeEntityObjectMgr.Query({
      model: "x => x.IsDeleted == false && x.SchemaVersionId == schemaVersionId",
      params: { schemaVersionId: activeVersionId }
    });
    requireSuccessfulServiceResponse(result, "Entity Objects could not be loaded.", coreStateRef.current);
    const resolvedObjects = getOperationItems(result)
      .filter((entityObject) => getValue(entityObject, "IsDeleted", "isDeleted") !== true)
      .sort((left, right) => getEntityObjectDisplayName(left).localeCompare(getEntityObjectDisplayName(right)))
      .map(withEntityObjectCardValues);
    return outboundEdiMode
      ? resolvedObjects.filter((entityObject) => matchesOutboundEdiEntityObject(entityObject, productEdiTransactionSets))
      : resolvedObjects;
  }, [outboundEdiMode, productEdiTransactionSets]);
  const loadEntityObjectsRef = useRef(loadEntityObjects);
  loadEntityObjectsRef.current = loadEntityObjects;

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!schemaId) {
        entityObjectsLoadKeyRef.current = "";
        pendingEntityObjectKeysRef.current.clear();
        dirtyEntityObjectKeysRef.current.clear();
        setSchema(null);
        setEntityObjects([]);
        setEntityObjectsLoadedKey("");
        setSelectedEntityObjectKey("");
        setCardEditorOpen(false);
        setLoading(false);
        return;
      }
      if (!schemaMgrRef.current?.getItem && !schemaMgrRef.current?.Query) {
        setLocalError("Schema service is not available.");
        setEntityObjectsLoadedKey(schemaId);
        setLoading(false);
        return;
      }
      if (!entityObjectMgrRef.current?.Query) {
        setLocalError("Entity Object service is not available.");
        setEntityObjectsLoadedKey(schemaId);
        setLoading(false);
        return;
      }
      if (entityObjectsLoadKeyRef.current === schemaId) {
        return;
      }
      entityObjectsLoadKeyRef.current = schemaId;
      setLoading(true);
      setEntityObjectsLoadedKey("");
      setLocalError("");
      try {
        const loadedSchema = await loadSchemaById(schemaMgrRef.current, schemaId, coreStateRef.current);
        if (!loadedSchema) throw new Error("The linked Entity Schema could not be loaded.");
        const objects = await loadEntityObjectsRef.current(loadedSchema);
        if (!mounted) return;
        pendingEntityObjectKeysRef.current.clear();
        dirtyEntityObjectKeysRef.current.clear();
        setSchema(loadedSchema);
        setEntityObjects(objects);
        setEntityObjectsLoadedKey(schemaId);
        setSelectedEntityObjectKey((previous) => {
          if (previous && objects.some((item, index) => String(getEntityObjectKey(item, index)) === String(previous))) {
            return previous;
          }
          return "";
        });
      } catch (ex) {
        if (mounted) {
          setEntityObjectsLoadedKey(schemaId);
          setLocalError(ex?.message || "Entity Objects could not be loaded.");
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };
    void load();
    return () => {
      mounted = false;
    };
  }, [entityObjectQueryServiceReady, schemaId, schemaServiceReady]);

  const refreshEntityObjects = useCallback(async () => {
    const objects = await loadEntityObjects(schemaRef.current);
    pendingEntityObjectKeysRef.current.clear();
    dirtyEntityObjectKeysRef.current.clear();
    setEntityObjects(objects);
    setSelectedEntityObjectKey((previous) => {
      if (previous && objects.some((item, index) => String(getEntityObjectKey(item, index)) === String(previous))) {
        return previous;
      }
      return "";
    });
    return objects;
  }, [loadEntityObjects]);

  const addEntityObjectDraft = useCallback(() => {
    if (!schema || !schemaVersionId) return;
    draftSequenceRef.current += 1;
    const draftEntityObject = createEntityObjectDraft(schema, schemaVersionId, entityObjectsRef.current, draftSequenceRef.current, productResourceDefaults);
    const draftKey = getEntityObjectKey(draftEntityObject);
    pendingEntityObjectKeysRef.current.add(String(draftKey));
    dirtyEntityObjectKeysRef.current.add(String(draftKey));
    onDirty?.();
    setEntityObjects((previous) => [...previous, draftEntityObject]);
    setSelectedEntityObjectKey(draftKey);
  }, [onDirty, productResourceDefaults, schema, schemaVersionId]);

  const updateEntityObject = useCallback((updatedEntityObject) => {
    const updatedKey = String(getEntityObjectKey(updatedEntityObject));
    dirtyEntityObjectKeysRef.current.add(updatedKey);
    pendingEntityObjectKeysRef.current.delete(updatedKey);
    onDirty?.();
    setEntityObjects((previous) => previous.map((entityObject, index) =>
      String(getEntityObjectKey(entityObject, index)) === updatedKey
        ? withEntityObjectCardValues(updatedEntityObject)
        : entityObject
    ));
  }, [onDirty]);

  const cancelEntityObjectEdit = useCallback((row, key) => {
    const entityObjectKey = String(key || getEntityObjectKey(row));
    if (!pendingEntityObjectKeysRef.current.has(entityObjectKey)) return;
    pendingEntityObjectKeysRef.current.delete(entityObjectKey);
    dirtyEntityObjectKeysRef.current.delete(entityObjectKey);
    setEntityObjects((previous) => previous.filter((entityObject, index) =>
      String(getEntityObjectKey(entityObject, index)) !== entityObjectKey
    ));
    setSelectedEntityObjectKey("");
  }, []);

  const runEntityObjectRedirectOperation = async (operationName, entityObject) => {
    if (!entityObjectMgr?.RunOperation) throw new Error("Entity Object operation service is not available.");
    const entityObjectId = getId(entityObject);
    if (!entityObjectId) throw new Error("Entity Object is missing an id.");
    const busyKey = `${operationName}:${entityObjectId}`;
    setActionBusyKey(busyKey);
    setLocalError("");
    try {
      const result = await entityObjectMgr.RunOperation({
        operationName,
        id: entityObjectId,
        queryParams: getResourceModeHardRedirectQueryParams(),
        item: entityObject,
        isItem: true
      });
      requireSuccessfulServiceResponse(result, "Entity Object action could not be opened.", coreState);
    } catch (ex) {
      setLocalError(ex?.message || "Entity Object action could not be opened.");
    } finally {
      setActionBusyKey("");
    }
  };

  const saveEntityObjects = useCallback(async () => {
    const activeMgr = entityObjectMgrRef.current;
    const activeSchema = schemaRef.current;
    const activeSchemaVersionId = getEditableDraftSchemaVersionId(activeSchema);
    if (!activeSchema || !activeSchemaVersionId) return entityObjectsRef.current;

    const currentRows = entityObjectsRef.current;
    const changedRows = currentRows.filter((entityObject, index) => {
      const key = String(getEntityObjectKey(entityObject, index));
      return dirtyEntityObjectKeysRef.current.has(key) || pendingEntityObjectKeysRef.current.has(key) || readValue(entityObject, "IsNew", "isNew") === true;
    });
    if (!changedRows.length || readOnly) {
      setLocalError("");
      return currentRows;
    }

    const rowsToSave = changedRows.map((entityObject) =>
      ensureEntityObjectDefaults(entityObject, activeSchema, activeSchemaVersionId, entityObjectMetadataRef.current, productResourceDefaultsRef.current)
    );
    const validationMessage = validateRequiredCardGridRows(rowsToSave, entityObjectValidationFields, "Entity Object");
    if (validationMessage) {
      setLocalError(validationMessage);
      throw buildWizardValidationError(validationMessage, { suppressSummary: true });
    }
    if (!activeMgr?.saveItem) throw new Error("Entity Object service is not available.");

    setLocalError("");
    const savedRows = [];
    for (const entityObjectToSave of rowsToSave) {
      const result = await activeMgr.saveItem(entityObjectToSave);
      requireSuccessfulServiceResponse(result, "Entity Object could not be saved.", coreStateRef.current);
      savedRows.push(
        getOperationItem(result)
        || getFirst(result?.Items)
        || getFirst(result?.items)
        || result?.Item
        || result?.item
        || entityObjectToSave
      );
    }

    const refreshed = await refreshEntityObjects();
    if (refreshed.length) return refreshed;

    const savedWithCardValues = savedRows.map(withEntityObjectCardValues);
    setEntityObjects(savedWithCardValues);
    return savedWithCardValues;
  }, [entityObjectValidationFields, readOnly, refreshEntityObjects]);

  const prepareValues = useCallback(async (currentValues = {}) => {
    const savedEntityObjects = await saveEntityObjects();
    const preparedEntityObjects = outboundEdiMode
      ? savedEntityObjects.filter((entityObject) => matchesOutboundEdiEntityObject(entityObject, productEdiTransactionSets))
      : savedEntityObjects;
    const entityObjectIds = preparedEntityObjects.map(getId).filter(Boolean);
    return {
      ...currentValues,
      SchemaId: schemaId,
      ActiveSchemaVersionId: schemaVersionId,
      EntityObjectIds: entityObjectIds,
      EntityObjectCount: preparedEntityObjects.length,
      ...(productEdiTransactionSets.length
        ? {
          DefaultTransactionSet: productEdiTransactionSets[0],
          DefaultTransactionSets: productEdiTransactionSets
        }
        : {})
    };
  }, [outboundEdiMode, productEdiTransactionSets, saveEntityObjects, schemaId, schemaVersionId]);

  useEffect(() => {
    registerStepController?.({
      prepareSave: prepareValues,
      prepareContinue: prepareValues
    });
    return () => registerStepController?.(null);
  }, [prepareValues, registerStepController]);

  const entityObjectValueDisplayResolvers = useMemo(() => ({
    Fields: (value, row) => {
      const count = getEntityObjectFieldCount(row);
      return `${count} field${count === 1 ? "" : "s"}`;
    },
    IsActive: (value) => (value === false ? "Inactive" : "Active")
  }), []);

  const entityObjectHeaderActions = useMemo(() => (
    readOnly ? [] : [
      {
        Key: "manage-entity-object",
        Title: "Manage",
        IconKey: "settings",
        Visible: (row) => Boolean(getId(row)),
        Disabled: (row) => saving || actionBusyKey === `${MODIFY_ENTITY_OPERATION}:${getId(row)}`,
        ActionCallback: (row) => runEntityObjectRedirectOperation(MODIFY_ENTITY_OPERATION, row)
      },
      {
        Key: "map-entity-object",
        Title: "Map",
        IconKey: "mapping",
        Visible: (row) => Boolean(getId(row)),
        Disabled: (row) => saving || actionBusyKey === `${ENTITY_OBJECT_FIELD_MAPPING_OPERATION}:${getId(row)}`,
        ActionCallback: (row) => runEntityObjectRedirectOperation(ENTITY_OBJECT_FIELD_MAPPING_OPERATION, row)
      }
    ]
  ), [actionBusyKey, readOnly, saving]);

  const entityObjectToolbarActions = useMemo(() => (
    readOnly ? [] : [{
      Key: "add-entity-object",
      Title: "Add Entity Object",
      Label: "Add Entity Object",
      IconKey: "add",
      Disabled: readOnly || saving || isLoading || !schema || !schemaVersionId || !entityObjectMetadata,
      ActionCallback: addEntityObjectDraft
    }]
  ), [addEntityObjectDraft, entityObjectMetadata, isLoading, readOnly, saving, schema, schemaVersionId]);

  const renderCardGrid = () => {
    if (isLoading) {
      return <div className="resource-wizard__linked-step-loading"><Loading /></div>;
    }
    if (!schema) {
      return <div className="resource-wizard__state">Create the Entity Schema before configuring Entity Objects.</div>;
    }
    if (!entityObjectMetadata) {
      return <div className="resource-wizard__state">Entity Object metadata is not available.</div>;
    }
    return (
      <CardGridControl
        rows={entityObjects}
        metadata={entityObjectMetadata}
        formMetadata={entityObjectMetadata}
        formFields={entityObjectFormFields}
        cardFields={entityObjectCardFields}
        rowKey={getEntityObjectKey}
        selectedRowKey={selectedEntityObjectKey}
        onSelectedRowKeyChange={setSelectedEntityObjectKey}
        onEditingChange={setCardEditorOpen}
        onCancelEdit={cancelEntityObjectEdit}
        onRowChange={updateEntityObject}
        headerIconActions={entityObjectHeaderActions}
        toolbarActions={entityObjectToolbarActions}
        readOnly={readOnly}
        disabled={saving}
        toggleEnabled={false}
        toggleFieldName="IsActive"
        activeFilterFieldName="IsActive"
        valueDisplayResolvers={entityObjectValueDisplayResolvers}
        emptyContent="No Entity Objects are configured yet."
        minCardWidth="15rem"
      />
    );
  };

  return (
    <div className="resource-wizard__linked-step">
      <LinkedStepHeader
        title={outboundEdiMode ? getEdiEntityLabel(productResourceDefaults) : `Entity Objects: ${getEntitySchemaModeLabel(wizard, getEntitySchemaMode(wizard, values, { preferSavedSetupMode: true }))}`}
        description={outboundEdiMode ? "Add existing outbound EDI Entity Objects or create a new outbound EDI document object for this transaction set." : "Add and manage Entity Objects defined for this schema."}
        countText={`${entityObjects.length} configured`}
      />
      {localError ? <div className="resource-wizard__inline-error">{localError}</div> : null}
      <div className="resource-wizard__linked-record-catalog resource-wizard__linked-record-catalog--compact">
        <div className="resource-wizard__linked-record-catalog-scroll">
          {renderCardGrid()}
        </div>
      </div>
      {!cardEditorOpen ? (
        <div className="resource-wizard__linked-step-actions">
          <div />
          <div className="resource-wizard__linked-step-actions-right">
            <ButtonControl type="standard-small" className="resource-wizard__button resource-wizard__button--secondary" onClick={() => onSave?.()} disabled={readOnly || saving || isLoading || !schema || !entityObjectMetadata} loading={saving}>Save</ButtonControl>
            <ButtonControl type="ok-small" className="resource-wizard__button resource-wizard__button--primary" onClick={() => onContinue?.()} disabled={readOnly || saving || isLoading || !schema || !entityObjectMetadata} loading={saving}>Save & Continue</ButtonControl>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default EntityObjectConfiguratorStep;
